import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterSidebar = ({ isOpen, onClose, filters, onFilterChange, onClearFilters }) => {
  const [priceRange, setPriceRange] = useState([0, 200]);
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { id: 'face-care', label: 'Face Care', count: 6 },
    { id: 'body-bar', label: 'Body Bar', count: 17 },
    { id: 'intimate-care', label: 'Intimate Care', count: 3 },
  ];

  // Brands intentionally omitted — Bounderlaster is a single-brand catalog.
  const brands = [];

  const ratings = [
    { id: '4-plus', label: '4+ Stars', count: 156 },
    { id: '3-plus', label: '3+ Stars', count: 298 },
    { id: '2-plus', label: '2+ Stars', count: 445 },
    { id: '1-plus', label: '1+ Stars', count: 498 }
  ];

  const skinTypes = [
    { id: 'oily', label: 'Oily', count: 89 },
    { id: 'dry', label: 'Dry', count: 76 },
    { id: 'combination', label: 'Combination', count: 134 },
    { id: 'sensitive', label: 'Sensitive', count: 67 },
    { id: 'normal', label: 'Normal', count: 98 }
  ];

  const handlePriceChange = (index, value) => {
    const newRange = [...priceRange];
    newRange[index] = parseInt(value);
    setPriceRange(newRange);
    onFilterChange('priceRange', newRange);
  };

  const handleCheckboxChange = (filterType, value, checked) => {
    const currentValues = filters?.[filterType] || [];
    let newValues;
    
    if (checked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues?.filter(v => v !== value);
    }
    
    onFilterChange(filterType, newValues);
  };

  // Calculate active filter count correctly
  const getActiveFilterCount = () => {
    let count = 0;
    
    // Count array filters (categories, brands, ratings, skinTypes)
    if (filters?.categories?.length > 0) count += filters?.categories?.length;
    if (filters?.brands?.length > 0) count += filters?.brands?.length;
    if (filters?.ratings?.length > 0) count += filters?.ratings?.length;
    if (filters?.skinTypes?.length > 0) count += filters?.skinTypes?.length;
    
    // Only count priceRange as 1 filter if it differs from default [0, 200]
    if (filters?.priceRange && 
        (filters?.priceRange?.[0] !== 0 || filters?.priceRange?.[1] !== 200)) {
      count += 1;
    }
    
    return count;
  };

  const FilterSection = ({ title, children, defaultOpen = true }) => {
    const [isExpanded, setIsExpanded] = useState(defaultOpen);
    
    return (
      <div className="border-b border-border pb-6 mb-6">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-between w-full mb-4 text-left"
        >
          <h3 className="text-sm font-medium text-foreground">{title}</h3>
          <Icon 
            name={isExpanded ? "ChevronUp" : "ChevronDown"} 
            size={16} 
            className="text-muted-foreground" 
          />
        </button>
        {isExpanded && children}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}
      {/* Sidebar */}
      <div className={`
        fixed lg:static top-0 left-0 z-50 h-full w-80 bg-background border-r border-border
        transform transition-transform duration-300 lg:transform-none overflow-y-auto lg:h-auto
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border lg:hidden">
          <h2 className="text-lg font-heading font-semibold text-foreground">Filters</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="px-6 py-2 lg:py-2">
          {/* Search */}
          <div className="mb-6">
            <Input
              type="search"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>

          {/* Clear Filters */}
          <div className="flex items-center justify-between mb-6">
            <span className="text-sm text-muted-foreground">
              {getActiveFilterCount()} {getActiveFilterCount() === 1 ? 'filter' : 'filters'} applied
            </span>
            <Button variant="ghost" size="sm" onClick={onClearFilters}>
              Clear All
            </Button>
          </div>

          {/* Price Range */}
          <FilterSection title="Price Range">
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={priceRange?.[0]}
                    onChange={(e) => handlePriceChange(0, e?.target?.value)}
                    className="text-center"
                  />
                </div>
                <span className="text-muted-foreground">to</span>
                <div className="flex-1">
                  <Input
                    type="number"
                    placeholder="Max"
                    value={priceRange?.[1]}
                    onChange={(e) => handlePriceChange(1, e?.target?.value)}
                    className="text-center"
                  />
                </div>
              </div>
              <div className="relative">
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={priceRange?.[0]}
                  onChange={(e) => handlePriceChange(0, e?.target?.value)}
                  className="absolute w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                />
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={priceRange?.[1]}
                  onChange={(e) => handlePriceChange(1, e?.target?.value)}
                  className="absolute w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                />
              </div>
            </div>
          </FilterSection>

          {/* Categories */}
          <FilterSection title="Categories">
            <div className="space-y-3">
              {categories?.map((category) => (
                <div key={category?.id} className="flex items-center justify-between">
                  <Checkbox
                    label={category?.label}
                    checked={filters?.categories?.includes(category?.id) || false}
                    onChange={(e) => handleCheckboxChange('categories', category?.id, e?.target?.checked)}
                  />
                  <span className="text-xs text-muted-foreground">({category?.count})</span>
                </div>
              ))}
            </div>
          </FilterSection>

          {/* Ratings */}
          <FilterSection title="Customer Rating">
            <div className="space-y-3">
              {ratings?.map((rating) => (
                <div key={rating?.id} className="flex items-center justify-between">
                  <Checkbox
                    label={rating?.label}
                    checked={filters?.ratings?.includes(rating?.id) || false}
                    onChange={(e) => handleCheckboxChange('ratings', rating?.id, e?.target?.checked)}
                  />
                  <span className="text-xs text-muted-foreground">({rating?.count})</span>
                </div>
              ))}
            </div>
          </FilterSection>

          {/* Skin Type */}
          <FilterSection title="Skin Type">
            <div className="space-y-3">
              {skinTypes?.map((type) => (
                <div key={type?.id} className="flex items-center justify-between">
                  <Checkbox
                    label={type?.label}
                    checked={filters?.skinTypes?.includes(type?.id) || false}
                    onChange={(e) => handleCheckboxChange('skinTypes', type?.id, e?.target?.checked)}
                  />
                  <span className="text-xs text-muted-foreground">({type?.count})</span>
                </div>
              ))}
            </div>
          </FilterSection>

          {/* Apply Filters Button (Mobile) */}
          <div className="lg:hidden mt-6">
            <Button fullWidth onClick={onClose}>
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;